import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getLocations,
  getLocation,
  getLocationOptions,
  createLocation,
  updateLocation,
  deleteLocation,
  getAccounts,
  searchAccountsPaginated,
  getAccountById,
  getLocationFormOptions,
  getLocationChangelog,
  uploadLocationImage,
  deleteLocationImage,
} from '../api';
import { locationKeys } from './queryKeys';
import type { LocationsFilters, CreateLocationInput, UpdateLocationInput } from '../types';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch locations with filters
 */
export function useLocations(filters: LocationsFilters = {}) {
  return useQuery({
    queryKey: locationKeys.list(filters),
    queryFn: () => getLocations(filters),
  });
}

/**
 * Hook to fetch a single location by ID
 */
export function useLocation(locationId: string | undefined) {
  return useQuery({
    queryKey: locationKeys.detail(locationId!),
    queryFn: () => getLocation(locationId!),
    enabled: !!locationId,
  });
}

/**
 * Hook to fetch location options (sites dropdown)
 */
export function useLocationOptions(accountId?: string, enabled = true) {
  return useQuery({
    queryKey: locationKeys.options(accountId),
    queryFn: () => getLocationOptions(accountId),
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to fetch accounts for filter dropdown
 */
export function useAccounts() {
  return useQuery({
    queryKey: locationKeys.accounts(),
    queryFn: getAccounts,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

const ACCOUNT_PAGE_SIZE = 50;

/**
 * Paginated account search with infinite scrolling.
 */
export function useLocationAccountsInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: locationKeys.accountsInfinite(search),
    queryFn: ({ pageParam = 0 }) =>
      searchAccountsPaginated({
        search,
        pageNumber: pageParam,
        pageLength: ACCOUNT_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Look up a single account by id for selected-label and dependent form data.
 */
export function useLocationAccount(accountId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: locationKeys.account(accountId ?? ''),
    queryFn: () => getAccountById(accountId!),
    enabled: enabled && !!accountId && accountId !== 'all',
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new location
 */
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationInput) => createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast.success('Location created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create location', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a location
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, data }: { locationId: string; data: UpdateLocationInput }) =>
      updateLocation(locationId, data),
    onSuccess: (_location, variables) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.locationId) });
      toast.success('Location updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update location', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a location
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) => deleteLocation(locationId),
    onSuccess: (_result, locationId) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId) });
      toast.success('Location deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete location', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// Form Options Hooks
// =============================================================================

/**
 * Hook to fetch form options (usergroups, transmitters, sensors)
 */
export function useLocationFormOptions(organisationId?: string, locationId?: string) {
  return useQuery({
    queryKey: locationKeys.formOptions(organisationId, locationId),
    queryFn: () => getLocationFormOptions(organisationId!, locationId),
    enabled: !!organisationId,
    staleTime: 30 * 1000, // 30 seconds - form data should be fresh
  });
}

// =============================================================================
// Changelog Hooks
// =============================================================================

/**
 * Hook to fetch changelog for a location
 */
export function useLocationChangelog(locationId?: string) {
  return useQuery({
    queryKey: locationKeys.changelog(locationId),
    queryFn: () => getLocationChangelog(locationId!),
    enabled: !!locationId,
    staleTime: 60 * 1000, // 1 minute
  });
}

// =============================================================================
// Image Hooks
// =============================================================================

/**
 * Hook to upload a location image
 */
export function useUploadLocationImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, file }: { locationId: string; file: File }) =>
      uploadLocationImage(locationId, file),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.locationId) });
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast.success('Image uploaded successfully');
    },
    onError: (error) => {
      toast.error('Failed to upload image', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a location image
 */
export function useDeleteLocationImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationId: string) => deleteLocationImage(locationId),
    onSuccess: (_result, locationId) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId) });
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast.success('Image removed successfully');
    },
    onError: (error) => {
      toast.error('Failed to remove image', { description: getErrorMessage(error) });
    },
  });
}
